import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useProcedures } from '../../../../procedures/hooks/useProcedures';
import { useSnackbar } from '../../../../../hooks/mui/useSnackBar';

const SAVE_DELAY_MS = {
  TEXT_INPUT: 1000,
  NUMBER_INPUT: 800,
  TABLE_GRID: 800,
  EQUIPMENT_INSPECTION: 800,
};

export function useTaskProcedureExecution({ task, taskId, onExecutionUpdate, onProcedureCompleted }) {
  const { t } = useTranslation(['tasks', 'common']);
  const { showError } = useSnackbar();
  const {
    submitStepResponse,
    startProcedureExecution,
    completeProcedureExecution,
    loading: procedureLoading
  } = useProcedures();

  const [expandedExecution, setExpandedExecution] = useState(null);
  const [stepValues, setStepValues] = useState({});
  const [submittingSteps, setSubmittingSteps] = useState(new Set());
  const [savedSteps, setSavedSteps] = useState(new Set());
  const [answeredSteps, setAnsweredSteps] = useState(new Set());
  const saveTimeoutRef = useRef({});

  // Auto-expand if execution is in progress
  useEffect(() => {
    if (task?.procedure?.executions?.length > 0) {
      const execution = task.procedure.executions[0];
      if (execution.status === 'in_progress') {
        setExpandedExecution(execution.execution_id);
      }
    }
  }, [task?.procedure?.executions]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(saveTimeoutRef.current).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, []);

  const handleAccordionChange = (executionId) => (event, isExpanded) => {
    setExpandedExecution(isExpanded ? executionId : null);
  };

  const handleSubmitStepResponse = useCallback(async (execution, step, value) => {
    const key = `${execution.execution_id}_${step.step_id}`;

    // Don't save an emptied required field as an answer; it stays as last saved
    if (!value && step.is_required) {
      return;
    }

    setSubmittingSteps(prev => new Set(prev).add(key));

    try {
      const responseData = {
        step_id: step.step_id,
        response_value: typeof value === 'string' ? value : JSON.stringify(value),
        file_id: null, // TODO: Handle file uploads
      };

      const result = await submitStepResponse(execution.execution_id, responseData);

      if (result.success) {
        // Tracked here instead of reloading the whole task after every save,
        // which re-rendered the page and made typing and clicking lag
        setAnsweredSteps(prev => (prev.has(key) ? prev : new Set(prev).add(key)));
        setSavedSteps(prev => new Set(prev).add(key));

        // Keep the saved indicator for 2 seconds
        setTimeout(() => {
          setSavedSteps(prev => {
            const newSet = new Set(prev);
            newSet.delete(key);
            return newSet;
          });
        }, 2000);
      } else {
        showError(result.error?.message || t('tasks:procedure.response_failed', 'Failed to save response'));
      }
    } catch (error) {
      showError(t('tasks:procedure.response_failed', 'Failed to save response'));
    } finally {
      setSubmittingSteps(prev => {
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
    }
  }, [submitStepResponse, showError, t]);

  // Stable across renders, so memoized steps only re-render when their own
  // value or save state changes
  const handleStepValueChange = useCallback((executionId, stepId, value, execution, step) => {
    const key = `${executionId}_${stepId}`;

    setStepValues(prev => ({
      ...prev,
      [key]: value
    }));

    // Clear existing timeout for this step
    if (saveTimeoutRef.current[key]) {
      clearTimeout(saveTimeoutRef.current[key]);
    }

    // Remove from saved steps indicator
    setSavedSteps(prev => {
      if (!prev.has(key)) return prev;
      const newSet = new Set(prev);
      newSet.delete(key);
      return newSet;
    });

    // Typing waits for a pause; clicks (yes/no, choices, files) save straight away
    saveTimeoutRef.current[key] = setTimeout(() => {
      handleSubmitStepResponse(execution, step, value);
    }, SAVE_DELAY_MS[step.step_type] ?? 150);
  }, [handleSubmitStepResponse]);

  const handleStartProcedureExecution = useCallback(async () => {
    if (!taskId) return;

    try {
      const result = await startProcedureExecution(taskId);
      if (result.success) {
        if (onExecutionUpdate) {
          await onExecutionUpdate();
        }
      } else {
        showError(result.error?.message || t('tasks:procedure.execution_failed', 'Failed to start procedure execution'));
      }
    } catch (error) {
      showError(t('tasks:procedure.execution_failed', 'Failed to start procedure execution'));
    }
  }, [taskId, startProcedureExecution, showError, onExecutionUpdate, t]);

  // No separate confirm: the task completion dialog that follows is the
  // confirmation step, so finishing a procedure is one click.
  const handleCompleteExecution = useCallback(async (executionId) => {
    try {
      const result = await completeProcedureExecution(executionId);
      if (result.success) {
        if (onExecutionUpdate) {
          await onExecutionUpdate();
        }
        onProcedureCompleted?.();
      } else {
        showError(result.error?.message || t('tasks:procedure.execution_complete_failed', 'Failed to complete procedure'));
      }
    } catch (error) {
      showError(t('tasks:procedure.execution_complete_failed', 'Failed to complete procedure'));
    }
  }, [completeProcedureExecution, showError, onExecutionUpdate, onProcedureCompleted, t]);

  return {
    expandedExecution,
    setExpandedExecution,
    stepValues,
    submittingSteps,
    savedSteps,
    answeredSteps,
    procedureLoading,
    handleAccordionChange,
    handleStepValueChange,
    handleSubmitStepResponse,
    handleStartProcedureExecution,
    handleCompleteExecution,
  };
}
